import React, { useState } from 'react';
import { 
  ClipboardDocumentIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { DNS_RECORDS, DNSRecord } from '../config/domainConfig';

const DNSRecordGenerator: React.FC = () => {
  const [copiedRecord, setCopiedRecord] = useState<string | null>(null);

  const copyToClipboard = async (text: string, recordName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedRecord(recordName);
      setTimeout(() => setCopiedRecord(null), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const getRecordTypeColor = (type: string) => {
    switch (type) {
      case 'TXT':
        return 'bg-blue-100 text-blue-800';
      case 'CNAME':
        return 'bg-green-100 text-green-800';
      case 'MX':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900">DNS Records for scaledots.com</h2>
        <p className="mt-2 text-gray-600">
          Copy these records to your DNS provider to complete the setup
        </p>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
        <div className="flex">
          <InformationCircleIcon className="h-5 w-5 text-blue-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Setup Instructions</h3>
            <div className="mt-2 text-sm text-blue-700">
              <ol className="list-decimal list-inside space-y-1">
                <li>Log in to your DNS provider (GoDaddy, Cloudflare, etc.)</li>
                <li>Navigate to DNS management for scaledots.com</li>
                <li>Add each record below exactly as shown</li>
                <li>Wait 5-10 minutes for DNS propagation</li>
                <li>Test your setup using the Domain Setup Tester</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      {/* DNS Records */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Required DNS Records</h3>
        
        {Object.entries(DNS_RECORDS).map(([key, record]) => (
          <div key={key} className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRecordTypeColor(record.type)}`}>
                  {record.type}
                </span>
                <span className="text-sm font-medium text-gray-900">{key}</span>
              </div>
              <button
                onClick={() => copyToClipboard(record.value, key)}
                className="inline-flex items-center px-2 py-1 border border-gray-300 rounded text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                <ClipboardDocumentIcon className="h-3 w-3 mr-1" />
                {copiedRecord === key ? 'Copied!' : 'Copy'}
              </button>
            </div>
            
            <div className="space-y-2">
              <div>
                <span className="text-sm font-medium text-gray-700">Name:</span>
                <div className="mt-1 bg-gray-50 rounded-md p-2">
                  <code className="text-sm text-gray-800">{record.name}</code>
                </div>
              </div>
              
              <div>
                <span className="text-sm font-medium text-gray-700">Value:</span>
                <div className="mt-1 bg-gray-50 rounded-md p-2">
                  <code className="text-sm text-gray-800 break-all">{record.value}</code>
                </div>
              </div>
              
              {record.priority && (
                <div>
                  <span className="text-sm font-medium text-gray-700">Priority:</span>
                  <span className="ml-2 text-sm text-gray-600">{record.priority}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Quick Copy All */}
      <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
        <h3 className="text-sm font-medium text-gray-900 mb-2">Quick Copy All</h3>
        <button
          onClick={() => {
            const allRecords = Object.entries(DNS_RECORDS)
              .map(([key, record]) => {
                let recordText = `${key}:\n`;
                recordText += `Type: ${record.type}\n`;
                recordText += `Name: ${record.name}\n`;
                recordText += `Value: ${record.value}\n`;
                if (record.priority) {
                  recordText += `Priority: ${record.priority}\n`;
                }
                recordText += `\n`;
                return recordText;
              })
              .join('');
            
            copyToClipboard(allRecords, 'all');
          }}
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <ClipboardDocumentIcon className="h-4 w-4 mr-2" />
          Copy All Records
        </button>
        {copiedRecord === 'all' && (
          <p className="mt-2 text-sm text-green-600 flex items-center">
            <CheckCircleIcon className="h-4 w-4 mr-1" />
            All records copied to clipboard!
          </p>
        )}
      </div>

      {/* Verification Steps */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
        <div className="flex">
          <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">After Adding Records</h3>
            <div className="mt-2 text-sm text-yellow-700">
              <ol className="list-decimal list-inside space-y-1">
                <li>Wait 5-10 minutes for DNS propagation</li>
                <li>Use <a href="https://dnschecker.org/" target="_blank" rel="noopener noreferrer" className="underline">DNS Checker</a> to verify records</li>
                <li>Test your setup using the Domain Setup Tester component</li>
                <li>Send a test email to verify everything works</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      {/* DNS Provider Help */}
      <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
        <h3 className="text-sm font-medium text-gray-900 mb-2">DNS Provider Help</h3>
        <div className="text-sm text-gray-600 space-y-1">
          <p><strong>GoDaddy:</strong> My Products → DNS → Manage → Add Record</p>
          <p><strong>Cloudflare:</strong> DNS → Records → Add Record</p>
          <p><strong>Namecheap:</strong> Domain List → Manage → Advanced DNS → Add New Record</p>
          <p><strong>Route 53:</strong> Hosted Zones → scaledots.com → Create Record</p>
        </div>
      </div>
    </div>
  );
};

export default DNSRecordGenerator;
